import {
  getMediaThumbnailUrl,
  normalizeMediaList,
} from '../../utils/postMediaUtils';

export const stripApi = (url) =>
  typeof url === 'string' ? url.replace(/\/api\/v1\/?$/, '') : '';

export const DEFAULT_PROOF_SHARE_BASE = 'https://api.joegalaxy.net';

export const getBackendOrigin = () =>
  stripApi(process.env.REACT_APP_AZURE_API_BASE_URL) ||
  stripApi(process.env.REACT_APP_API_BASE_URL_PROD) ||
  stripApi(process.env.REACT_APP_API_BASE_URL) ||
  'http://localhost:4000';

export const getProofShareOrigin = () =>
  stripApi(process.env.REACT_APP_SHARE_BASE_URL) ||
  stripApi(process.env.REACT_APP_AZURE_API_BASE_URL) ||
  stripApi(process.env.REACT_APP_API_BASE_URL_PROD) ||
  stripApi(process.env.REACT_APP_API_BASE_URL) ||
  DEFAULT_PROOF_SHARE_BASE;

export const safeText = (value, fallback = '') => {
  const clean = String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
  return clean || fallback;
};

export const isInternalObjectIdText = (value) =>
  /^[a-f0-9]{24}$/i.test(String(value || '').trim());

export const PROOF_ATTRIBUTION_VERSION = '1';

export const PROOF_ORIGIN_SURFACES = Object.freeze({
  APR: 'apr',
  PROOF_RICH_SHARE: 'proof_rich_share',
  PROOF_PORTFOLIO: 'proof_portfolio',
  PROFILE_PROOF_PORTFOLIO: 'profile_proof_portfolio',
});

const VALID_PROOF_ORIGIN_SURFACES = new Set(
  Object.values(PROOF_ORIGIN_SURFACES),
);

const cleanUrlParamValue = (value, max = 160) =>
  String(value || '')
    .replace(/[\r\n\t]+/g, ' ')
    .trim()
    .slice(0, max);

export const normalizePublicIdentityText = (value) => {
  const clean = safeText(value);
  if (!clean || isInternalObjectIdText(clean)) return '';
  if (/^[^@\s]+@[^@\s]+$/.test(clean)) return '';
  return clean;
};

export const normalizeProofOriginSurface = (
  value,
  fallback = PROOF_ORIGIN_SURFACES.PROOF_PORTFOLIO,
) => {
  const clean = cleanUrlParamValue(value, 80);
  return VALID_PROOF_ORIGIN_SURFACES.has(clean) ? clean : fallback;
};

export const isSafePublicPlayerId = (value) => {
  const clean = normalizePublicIdentityText(value).replace(/^@+/, '');
  if (!clean || isInternalObjectIdText(clean)) return false;
  return /^[A-Za-z0-9_-]{3,40}$/.test(clean);
};

export const normalizeProofRefPlayerId = (value) => {
  const clean = normalizePublicIdentityText(value).replace(/^@+/, '');
  return isSafePublicPlayerId(clean) ? clean : '';
};

export const normalizePublicHandle = (...values) => {
  const raw = values
    .map((value) => normalizePublicIdentityText(value).replace(/^@+/, ''))
    .find((value) => value && !isInternalObjectIdText(value));
  return raw ? `@${raw}` : '';
};

export const getProofAttributionRefPlayerId = (
  proof,
  fallbackRefPlayerId = '',
) =>
  [
    proof?.proofAttribution?.refPlayerId,
    proof?.attribution?.refPlayerId,
    proof?.helper?.playerId,
    fallbackRefPlayerId,
  ]
    .map(normalizeProofRefPlayerId)
    .find(Boolean) || '';

export const buildProofAttributedPostTaskUrl = ({
  proofId,
  refPlayerId,
  source,
  originSurface,
  regionSlug,
  extraParams = {},
} = {}) => {
  const params = new URLSearchParams();
  params.set('intent', 'post_task');

  const safeSource = cleanUrlParamValue(source, 120);
  if (safeSource) params.set('source', safeSource);

  const safeProofId = cleanUrlParamValue(proofId, 120);
  if (safeProofId) params.set('proofId', safeProofId);

  const safeRefPlayerId = normalizeProofRefPlayerId(refPlayerId);
  if (safeRefPlayerId) params.set('refPlayerId', safeRefPlayerId);

  params.set(
    'originSurface',
    normalizeProofOriginSurface(originSurface),
  );
  params.set('attributionVersion', PROOF_ATTRIBUTION_VERSION);

  const safeRegionSlug = cleanUrlParamValue(regionSlug, 120);
  if (safeRegionSlug) params.set('regionSlug', safeRegionSlug);

  Object.entries(extraParams || {}).forEach(([key, value]) => {
    const safeKey = cleanUrlParamValue(key, 80);
    const safeValue = cleanUrlParamValue(value, 512);
    if (!safeKey || !safeValue) return;
    params.set(safeKey, safeValue);
  });

  return `/playermap?${params.toString()}`;
};

export const formatDuration = (value) => {
  const totalSeconds = Number(value);
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '';
  const rounded = Math.round(totalSeconds);
  if (rounded < 60) return `${rounded}s`;
  const minutes = Math.floor(rounded / 60);
  const seconds = rounded % 60;
  if (minutes < 60) return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

export const formatAmount = (proof) => {
  const amount = Number(proof?.amountUSD);
  if (Number.isFinite(amount) && amount > 0) {
    return `$${amount.toLocaleString(undefined, {
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    })}`;
  }
  const mode = safeText(proof?.paymentMode);
  if (mode === 'community') return 'Community task';
  if (mode === 'paid') return 'Paid task';
  return '';
};

export const normalizeProofMediaList = (proof) => {
  const seen = new Set();
  const media = [];
  const pushMedia = (item) => {
    if (!item) return;
    const url =
      typeof item === 'string'
        ? item
        : item.url ||
          item.secure_url ||
          item.secureUrl ||
          item.src ||
          item.mediaUrl;
    if (!url || seen.has(url)) return;
    seen.add(url);
    media.push(item);
  };
  (Array.isArray(proof?.media) ? proof.media : []).forEach(pushMedia);
  (Array.isArray(proof?.safeMedia) ? proof.safeMedia : []).forEach(pushMedia);
  if (proof?.mediaUrl) pushMedia({ url: proof.mediaUrl });
  return normalizeMediaList({ media });
};

export const resolveCoverIndex = (proof, mediaList) => {
  const coverIndex = Number(proof?.coverIndex);
  if (
    Number.isInteger(coverIndex) &&
    coverIndex >= 0 &&
    coverIndex < mediaList.length
  ) {
    return coverIndex;
  }
  return 0;
};

export const getProofCoverMedia = (proof) => {
  const mediaList = normalizeProofMediaList(proof);
  return mediaList[resolveCoverIndex(proof, mediaList)] || null;
};

export const getProofPreviewUrl = (media) => {
  if (!media?.url) return '';
  if (media.type === 'video') {
    return media.thumbnailUrl || media.posterUrl || getMediaThumbnailUrl(media) || '';
  }
  return media.url;
};

export const buildReceiptPath = (proof) =>
  safeText(proof?.sharePath) ||
  (proof?.proofId ? `/share/localops/proof/${proof.proofId}` : '');

export const buildPublicProofUrl = (proof) => {
  const richUrl = safeText(proof?.richShareUrl || proof?.canonicalShareUrl);
  if (richUrl) {
    if (/^https?:\/\//i.test(richUrl)) return richUrl;
    return new URL(
      richUrl.startsWith('/') ? richUrl : `/${richUrl}`,
      getProofShareOrigin(),
    ).toString();
  }
  const path = buildReceiptPath(proof);
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return new URL(
    path.startsWith('/') ? path : `/${path}`,
    getProofShareOrigin(),
  ).toString();
};

export const buildPostTaskPath = (
  proof,
  source = 'proof_portfolio',
  {
    originSurface = PROOF_ORIGIN_SURFACES.PROOF_PORTFOLIO,
    fallbackRefPlayerId = '',
  } = {},
) => {
  const receiptPath = buildReceiptPath(proof);
  return buildProofAttributedPostTaskUrl({
    proofId: proof?.proofAttribution?.proofId || proof?.proofId,
    refPlayerId: getProofAttributionRefPlayerId(proof, fallbackRefPlayerId),
    source,
    originSurface,
    regionSlug: proof?.proofAttribution?.regionSlug || proof?.regionSlug,
    extraParams: receiptPath ? { proof: receiptPath } : {},
  });
};

export const getProofTitle = (proof) =>
  safeText(
    proof?.actualTaskTitle ||
      proof?.publicTaskTitle ||
      proof?.taskTitle ||
      proof?.title ||
      proof?.taskLabel,
    'Verified LocalOps completion',
  );

export const getPlayerDisplay = (player) =>
  normalizePublicHandle(player?.handle, player?.playerId) ||
  normalizePublicIdentityText(player?.displayName) ||
  'LocalOps helper';
